"use client";

import { createBrowserSupabase } from "@/lib/supabase/browser";
import { terjemahkanErrorRpc } from "@/lib/rpcError";
import {
  fetchAllProductVariants,
  fetchAllRows
} from "@/lib/supabase/fetchAllVariants";
import { selectInChunks } from "@/lib/inChunks";
import type { CustomerGroup } from "@/lib/reseller-groups";
import {
  ACTIVE_INVOICE_STATUSES,
  billedLinesAgree,
  customersNeedingGroup,
  groupFromRow,
  hasPriceAxis,
  isEmptyUnitPrice,
  resolveSnapshotPriceParts,
  variantPricingFromRow,
  type BilledLine,
  type VariantPricing
} from "@/lib/backfillUnitPrice";
import { levelForProduct, type GroupBrandRule } from "@/lib/groupBrandDiscount";
import {
  attachGroupBrandRules,
  memoGroupBrandRules,
  type GroupRulesOptions
} from "@/lib/supabase/groupBrandDiscount";

type BrowserDb = NonNullable<ReturnType<typeof createBrowserSupabase>>;

// `is_active`: re-snapshot mengutamakan varian aktif sebagai perwakilan harga
// (`VariantPricing.isActive`, lihat `resolveSnapshotUnitPrice`). Di checkout
// RLS pembeli memang hanya memulangkan varian aktif, jadi kolom ini tak
// mengubah apa pun di sana.
const VARIANT_PRICING_COLUMNS =
  "id, name, sku, option_ids, price_normal, price_extra, diskon_mode, diskon_reseller, diskon_agen, diskon_slot3, diskon_slot4, diskon_slot5, diskon_slot6, diskon_slot7, diskon_slot8, diskon_slot9, diskon_slot10, diskon_slot11, diskon_slot12, diskon_slot13, diskon_slot14, diskon_slot15, is_active";

/** Varian (dengan field harga) satu produk → bentuk yang dipahami pricing.ts. */
export async function fetchProductVariantPricing(
  db: BrowserDb,
  productId: string
): Promise<VariantPricing[]> {
  // Ambil SEMUA varian secara TERPAGINASI: produk dengan >1000 kombinasi
  // (Warna×Motif×Ukuran) menembus batas PostgREST `max-rows` (1000). Query satu-
  // tembak lama hanya membawa 1000 varian pertama, sehingga varian di luar itu
  // gagal di-resolve saat checkout — `matchVariant` (id lalu nama) tak menemukannya
  // → error "Variasi sudah tidak tersedia" MESKI varian tampil di selektor produk
  // (halaman detail SUDAH terpaginasi). Cermin `fetchClosedPOEditContext` &
  // `getPublicProductBySlug` yang sudah terpaginasi. Melempar pada error DB —
  // pemanggil (checkout / edit OPEN PO / re-snapshot) menangani via fallback.
  const rows = await fetchAllProductVariants<Record<string, any>>(
    db,
    productId,
    VARIANT_PRICING_COLUMNS
  );
  return (rows as any[]).map(variantPricingFromRow);
}

/**
 * Produk ber-SUMBU harga? (mig 7680) — dibaca langsung dari kolom
 * `price_affecting_groups` (sesi admin memegang grant-nya; anon tidak).
 * Gagal/absen → `null` = tak diketahui → `resolveSnapshotUnitPrice`
 * fail-closed (varian tanpa harga sendiri tetap harga menyusul).
 */
export async function fetchProductPriceAxis(
  db: BrowserDb,
  productId: string
): Promise<boolean | null> {
  const { data, error } = await db
    .from("products")
    .select("price_affecting_groups")
    .eq("id", productId)
    .maybeSingle();
  if (error || !data) return null;
  return hasPriceAxis(
    (data as { price_affecting_groups?: unknown }).price_affecting_groups
  );
}

/**
 * `products.price_normal` — lapis TERAKHIR model harga ("harga dasar =
 * price_normal varian, fallback price_normal produk"). Dibutuhkan resolver
 * checkout supaya harga yang dikunci sama persis dengan yang dilihat pembeli.
 * Best-effort: gagal/absen → undefined (= tak ada lapis terakhir), tak melempar.
 */
export async function fetchProductPriceNormal(
  db: BrowserDb,
  productId: string
): Promise<number | undefined> {
  const { data, error } = await db
    .from("products")
    .select("price_normal")
    .eq("id", productId)
    .maybeSingle();
  if (error || !data) return undefined;
  const raw = (data as { price_normal?: number | string | null }).price_normal;
  if (raw === null || raw === undefined) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Merek & penanda Non Web satu produk — kunci aturan diskon merek per level
 * (mig 7070, `levelForProduct`). Galat → THROW: tanpa merek, aturan merek tak
 * bisa dipastikan, dan jalur uang tak boleh menyimpulkan "tanpa aturan".
 */
export async function fetchProductBrandMeta(
  db: BrowserDb,
  productId: string
): Promise<{ brand: string | null; isManual: boolean }> {
  const { data, error } = await db
    .from("products")
    .select("brand, is_manual")
    .eq("id", productId)
    .maybeSingle();
  if (error) throw new Error(terjemahkanErrorRpc(error).pesan);
  const row = (data ?? {}) as { brand?: string | null; is_manual?: boolean | null };
  return { brand: row.brand ?? null, isManual: row.is_manual === true };
}

/**
 * Grup reseller satu pelanggan (null = Customer biasa / grup nonaktif).
 *
 * Error QUERY → THROW (bughunt H2-1, DITAHAN/HELD): sebelumnya disamakan
 * diam-diam dengan "tidak punya grup", sehingga kegagalan transien membuat
 * reseller ber-diskon aktif tersnapshot harga NORMAL secara permanen.
 * Pemanggil WAJIB menangani throw ini secara sadar (skip/gagalkan aksi),
 * BUKAN menangkapnya lalu diam-diam memakai `group=null`.
 */
export async function fetchCustomerGroup(
  db: BrowserDb,
  customerId: string,
  opts: GroupRulesOptions = {}
): Promise<CustomerGroup> {
  const { data, error } = await db
    .from("customers")
    .select(
      "reseller_groups(id, slot, name, discount_persen, is_active)"
    )
    .eq("id", customerId)
    .maybeSingle();
  if (error) throw new Error(terjemahkanErrorRpc(error).pesan);
  // Diskon merek per level (mig 7070) ikut dimuat — galatnya THROW juga.
  return attachGroupBrandRules(db, groupFromRow((data as any)?.reseller_groups ?? null), opts);
}

/**
 * Grup reseller BANYAK pelanggan dalam satu kueri ber-potongan (`selectInChunks`
 * — potongan paralel, satu round-trip) — versi batch `fetchCustomerGroup` untuk
 * loop re-snapshot (uji nyata K7). Tidak melempar: pelanggan yang grupnya tak
 * bisa dipastikan masuk `failed` (bughunt H2-1 — pemanggil MELEWATI barisnya,
 * bukan menganggapnya tanpa grup). Kueri gagal = semua id gagal.
 *
 * Pelanggan yang tak kembali di hasil (terhapus / tak terlihat) = tanpa grup,
 * sama dengan `maybeSingle()` kosong di `fetchCustomerGroup`.
 */
export async function fetchCustomerGroups(
  db: BrowserDb,
  customerIds: readonly string[],
  opts: GroupRulesOptions = {}
): Promise<{ groups: Map<string, CustomerGroup>; failed: Set<string> }> {
  const groups = new Map<string, CustomerGroup>();
  const failed = new Set<string>();
  const ids = Array.from(new Set(customerIds));
  if (!ids.length) return { groups, failed };
  let rows: Array<{ id: string; reseller_groups?: unknown }>;
  try {
    rows = (await selectInChunks(ids, (part) =>
      db
        .from("customers")
        .select("id, reseller_groups(id, slot, name, discount_persen, is_active)")
        .in("id", part)
    )) as Array<{ id: string; reseller_groups?: unknown }>;
  } catch {
    for (const id of ids) failed.add(id);
    return { groups, failed };
  }
  const byId = new Map(rows.map((row) => [row.id, row]));
  for (const id of ids) {
    try {
      // Aturan merek ber-memo (`opts.rules`) → satu RPC untuk seluruh daftar.
      groups.set(
        id,
        await attachGroupBrandRules(db, groupFromRow((byId.get(id)?.reseller_groups as any) ?? null), opts)
      );
    } catch {
      failed.add(id);
    }
  }
  return { groups, failed };
}

/**
 * Grup reseller pembeli yang sedang login (untuk edit pesanan miliknya).
 * Error QUERY → THROW -- lihat komentar `fetchCustomerGroup` (bughunt H2-1).
 *
 * `rulesFor` (opsional) = sumber aturan merek per pelanggan, mis. pemuat
 * ber-cadangan checkout (`loadBrandRulesForCheckout`, pola A5) — bughunt
 * D13-3. Absen = RPC aturan langsung (strict, melempar).
 */
export async function fetchMyCustomerGroup(
  db: BrowserDb,
  opts: { rulesFor?: (customerId: string) => Promise<GroupBrandRule[]> } = {}
): Promise<CustomerGroup> {
  const {
    data: { user }
  } = await db.auth.getUser();
  if (!user) return null;
  const { data, error } = await db
    .from("customers")
    .select("id, reseller_groups(id, slot, name, discount_persen, is_active)")
    .eq("auth_user_id", user.id)
    .maybeSingle();
  if (error) throw new Error(terjemahkanErrorRpc(error).pesan);
  const customerId = (data as { id?: string } | null)?.id;
  const rulesFor = opts.rulesFor;
  return attachGroupBrandRules(
    db,
    groupFromRow((data as any)?.reseller_groups ?? null),
    rulesFor && customerId ? { rules: () => rulesFor(customerId) } : {}
  );
}

export type ResnapshotResult = {
  /** Baris yang nilainya benar-benar diubah di DB. */
  updated: number;
  /** Baris yang dilewati (harga menyusul / sudah sama / berselisih nota / gagal tulis). */
  skipped: number;
  /** Baris yang diperiksa (sesudah filter onlyNullOrZero). */
  scanned: number;
  /**
   * Bagian dari `skipped`: baris yang SUDAH ternota dengan nominal yang
   * berbeda dari harga katalog (atau notanya gagal dibaca) — dibiarkan NULL,
   * lihat `billedLinesAgree`.
   */
  tagihanConflict: number;
};

/**
 * Hitung ulang unit_price order_items aktif satu produk via pricing.ts lalu tulis
 * ke DB. Dipakai setelah admin menyimpan harga produk (ProductForm) supaya order
 * yang masih NULL terisi begitu harga diinput. Best-effort — kegagalan tidak
 * melempar (return hasil sebagian).
 *
 * Aturan harganya = checkout (`resolveSnapshotUnitPrice`): varian pesanan,
 * lalu varian perwakilan bila harga varian SERAGAM, lalu `products.price_normal`;
 * harga varian campuran tetap harga menyusul, begitu pula varian tanpa harga
 * sendiri pada produk ber-sumbu harga (mig 7680).
 *
 * Pemanggil: Simpan form produk (ProductForm) DAN Atur Harga di Detail
 * Produksi (`saveVariantPricesInSupabase`) — satu aturan untuk keduanya.
 *
 * Baris yang sudah ternota hanya ditulis bila SETIAP baris nota aktifnya
 * menagih tepat harga katalog × qty (`billedLinesAgree`, putusan pemilik
 * 2026-10-20); nota gagal dibaca → baris ber-harga hasil hitung dilewati semua.
 *
 * `onlyNullOrZero` (default true): hanya mengisi baris yang belum berharga
 * (NULL/0) → harga yang sudah disepakati tak ditimpa & aman dijalankan ulang.
 */
export async function resnapshotOrderItemsForProduct(
  productId: string,
  opts: { onlyNullOrZero?: boolean } = {}
): Promise<ResnapshotResult> {
  const onlyNullOrZero = opts.onlyNullOrZero ?? true;
  const result: ResnapshotResult = { updated: 0, skipped: 0, scanned: 0, tagihanConflict: 0 };

  const db = createBrowserSupabase();
  if (!db) return result;

  try {
    // Lima bacaan di bawah saling lepas → PARALEL, satu round-trip (uji nyata
    // K7: dulu berurutan, ±1 detik masing-masing dari luar Asia).
    const [variants, productMeta, productPriceNormal, priceAxis, orderRows] = await Promise.all([
      fetchProductVariantPricing(db, productId),
      // Merek produk → aturan diskon merek per level (mig 7070). Gagal dibaca →
      // throw → seluruh re-snapshot berhenti (catch di bawah), bukan mengisi
      // harga level tanpa aturan merek.
      fetchProductBrandMeta(db, productId),
      // Lapis TERAKHIR model harga, seperti checkout. Gagal dibaca → undefined →
      // produk tanpa varian berharga tetap NULL (tak menebak), tak melempar.
      fetchProductPriceNormal(db, productId),
      // Sumbu harga (mig 7680) — aturan yang SAMA dengan checkout; tak
      // diketahui → null → fail-closed.
      fetchProductPriceAxis(db, productId),
      // Orders satu produk bisa >1000 (produk sangat populer / banyak pembeli) →
      // query top-level kena max-rows. Ambil TERPAGINASI agar semua order
      // ter-re-snapshot (urut id agar range stabil).
      fetchAllRows<any>((from, to) =>
        db
          .from("orders")
          .select("customer_id, order_items(id, variant_id, variant_name, unit_price, unit_price_extra, voucher_usage_id, voucher_name, manual_price_normal, manual_discount_mode, order_bundle_id, gift_role)")
          .eq("product_id", productId)
          .neq("status", "Batal")
          .order("id", { ascending: true })
          .range(from, to)
      )
    ]);
    if (!variants.length) return result;
    const rules = memoGroupBrandRules(db);

    // Grup hanya untuk pemesan yang punya baris kosong, dalam SATU kueri batch
    // (uji nyata K7: dulu satu request berurutan per pemesan, kosong atau
    // tidak). bughunt H2-1 (HELD): pelanggan yang grupnya gagal dimuat masuk
    // `groupFetchFailed` → baris-barisnya DILEWATI, bukan ditulis dengan
    // `group=null` yang salah.
    const { groups: groupCache, failed: groupFetchFailed } = await fetchCustomerGroups(
      db,
      customersNeedingGroup((orderRows as any[]) ?? [], onlyNullOrZero),
      { rules }
    );
    // Baris yang harganya sudah dihitung & berbeda dari tersimpan — ditulis
    // SESUDAH notanya diperiksa (satu query ber-potongan, bukan per baris).
    const pending: Array<{ id: string; price: number; priceExtra: number }> = [];

    for (const order of ((orderRows as any[]) ?? [])) {
      const customerId = (order.customer_id as string | null) ?? null;
      const group: CustomerGroup = customerId ? groupCache.get(customerId) ?? null : null;

      if (customerId && groupFetchFailed.has(customerId)) {
        // Grup tak bisa dipastikan → lewati SELURUH baris order ini daripada
        // menyimpulkan "tanpa grup" yang mungkin salah. Baris ini akan dicoba
        // lagi lain kali fungsi ini dijalankan (mis. admin edit harga lagi).
        result.skipped += (order.order_items as any[] ?? []).length;
        continue;
      }

      for (const item of ((order.order_items as any[]) ?? [])) {
        // Baris ber-voucher TIDAK PERNAH di-resnapshot (audit 2026-09-10).
        // `unit_price`-nya dibentuk `admin_set_order_voucher` / checkout
        // ber-voucher dan sudah neto; sejak plafon persen boleh kosong (mig
        // 5660) voucher 100 % bahkan menghasilkan 0 yang SAH — yang dibaca
        // `isEmptyUnitPrice` sebagai "belum berharga". Menulisnya ulang ke harga
        // level penuh menghapus potongan sementara jejak vouchernya tetap
        // terpasang: pembeli ditagih penuh, laporan tetap mengklaim potongan.
        if (item.voucher_usage_id || (item.voucher_name ?? "").toString().trim()) {
          result.skipped += 1;
          continue;
        }
        // Harga yang diatur admin di keranjang (mig 7060: Harga Jual baris /
        // diskon manual) juga TIDAK PERNAH di-resnapshot — diskon manual 100 %
        // menghasilkan Rp 0 yang SAH, dan resnapshot dari harga varian akan
        // membuang Harga Jual baris.
        if (item.manual_price_normal != null || item.manual_discount_mode != null) {
          result.skipped += 1;
          continue;
        }
        // Baris BUNDEL (mig 8040) juga TIDAK PERNAH di-resnapshot — harganya
        // alokasi harga bundel (di bawah harga level, sah) yang hanya ditulis
        // RPC checkout bundel; menimpanya ke harga level menghapus potongan
        // bundel sementara jejak bundelnya tetap terpasang.
        if (item.order_bundle_id) {
          result.skipped += 1;
          continue;
        }
        // Baris HADIAH pembelian (mig 8680) juga tidak: Rp 0 yang SAH (bukan
        // "harga menyusul"), dan DB menolak harga lain di baris itu.
        if (item.gift_role === "hadiah") {
          result.skipped += 1;
          continue;
        }
        if (onlyNullOrZero && !isEmptyUnitPrice(item.unit_price)) continue;
        result.scanned += 1;
        // Pagar `customersNeedingGroup`: pemesan yang grupnya TIDAK dimuat tak
        // boleh dihargai sebagai pelanggan tanpa grup (harga normal untuk
        // reseller) — lewati, dicoba lagi pada Simpan berikutnya.
        if (customerId && !groupCache.has(customerId)) {
          result.skipped += 1;
          continue;
        }

        const price = resolveSnapshotPriceParts(
          {
            variantId: (item.variant_id as string | null) ?? null,
            variantName: item.variant_name as string
          },
          variants,
          levelForProduct(group, productMeta),
          productPriceNormal,
          priceAxis
        );

        // Harga menyusul / sudah sama (harga DAN potret tambahannya) → tak perlu menulis.
        if (
          price === null ||
          (Number(item.unit_price) === price.unitPrice &&
            Number(item.unit_price_extra ?? 0) === price.priceExtra)
        ) {
          result.skipped += 1;
          continue;
        }
        pending.push({ id: item.id as string, price: price.unitPrice, priceExtra: price.priceExtra });
      }
    }

    if (!pending.length) return result;

    // Nota AKTIF baris-baris itu. Gagal dibaca = TIDAK TAHU → tak satu pun
    // ditulis (fail-closed; menulis harga di bawah nota yang tak terbaca bisa
    // melahirkan dua harga untuk barang yang sama).
    const billed = new Map<string, BilledLine[]>();
    try {
      const lines = await selectInChunks(
        pending.map((p) => p.id),
        (part) =>
          db
            .from("invoice_lines")
            .select("order_item_id, qty, amount, unit_price_extra, invoices!inner(status)")
            .in("order_item_id", part)
            .eq("kind", "product_item")
            .is("cancelled_at", null)
            .in("invoices.status", [...ACTIVE_INVOICE_STATUSES])
      );
      for (const line of lines as Array<{ order_item_id: string; unit_price_extra?: number | string | null } & BilledLine>) {
        const list = billed.get(line.order_item_id) ?? [];
        list.push({ qty: line.qty, amount: line.amount, unitPriceExtra: line.unit_price_extra ?? null });
        billed.set(line.order_item_id, list);
      }
    } catch {
      result.skipped += pending.length;
      result.tagihanConflict += pending.length;
      return result;
    }

    for (const { id, price, priceExtra } of pending) {
      // Nominal DAN pecahan dasar/tambahannya wajib sama dengan nota (bughunt D14-32).
      if (!billedLinesAgree(price, billed.get(id) ?? [], priceExtra)) {
        result.skipped += 1;
        result.tagihanConflict += 1;
        continue;
      }
      // Syarat "masih kosong" ditegakkan lagi di WHERE (idiom route backfill):
      // baris yang terisi sejak dipindai tidak ikut tertimpa. `.select("id")`
      // membedakan 0 baris cocok dari sukses — UPDATE tanpa baris tak melempar.
      // mig 7900: potret tambahan ditulis BERSAMA harga — satu sumber (varian
      // yang sama) untuk keduanya.
      let q = db.from("order_items").update({ unit_price: price, unit_price_extra: priceExtra }).eq("id", id);
      if (onlyNullOrZero) q = q.or("unit_price.is.null,unit_price.eq.0");
      // mig 8040: baris BUNDEL tak pernah ditulis — pagar kedua di WHERE.
      q = q.is("order_bundle_id", null);
      // mig 8680: baris HADIAH tak pernah ditulis — pagar kedua di WHERE.
      q = q.or("gift_role.is.null,gift_role.neq.hadiah");
      const { data, error } = await q.select("id");
      if (error || !((data as unknown[]) ?? []).length) result.skipped += 1;
      else result.updated += 1;
    }
  } catch {
    // Best-effort: kembalikan hasil sebagian, jangan ganggu alur pemanggil.
  }

  return result;
}
